import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/system';
import { generateAITokenPattern } from '../../utils/aiTokens';

const binaryPulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.9;
  }
`;

const pulseGradientBorderLight = keyframes`
  0%, 100% {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05), 0 0 6px rgba(148, 75, 168, 0.2);
  }
  50% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 0 12px rgba(148, 75, 168, 0.4);
  }
`;

const pulseGradientBorderDark = keyframes`
  0%, 100% {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2), 0 0 6px rgba(178, 120, 200, 0.25);
  }
  50% {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 0 12px rgba(178, 120, 200, 0.5);
  }
`;

const AILoadingAnimation = ({
  messages = ['Processing...', 'Analyzing...', 'Generating...', 'Finalizing...'],
  showMessage = true,
  containerSx = {},
  tokenCount = 100,
  className = '',
}) => {
  const [tokenPattern, setTokenPattern] = useState(() => generateAITokenPattern(tokenCount));
  const [currentMessage, setCurrentMessage] = useState(messages[0]);

  // Cycle through messages
  useEffect(() => {
    if (!showMessage) return;

    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % messages.length;
      setCurrentMessage(messages[messageIndex]);
    }, 2000);

    return () => clearInterval(messageInterval);
  }, [messages, showMessage]);

  return (
    <Box
      className={className}
      sx={{
        position: 'relative',
        minHeight: showMessage ? '35px' : '30px',
        bgcolor: 'background.paper',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        ...containerSx,
      }}
    >
      {/* Optional floating message panel */}
      {showMessage && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            height: '38px',
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(178, 120, 200, 0.3)'
                : 'rgba(148, 75, 168, 0.2)',
            paddingLeft: '12px',
            paddingRight: '16px',
            backdropFilter: 'blur(8px)',
            zIndex: 2,
            animation: (theme) =>
              `${theme.palette.mode === 'dark' ? pulseGradientBorderDark : pulseGradientBorderLight} 2s ease-in-out infinite`,
          }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            {currentMessage}
          </Typography>
        </Box>
      )}

      {/* AI Tokens grid background - only show if tokenCount > 0 */}
      {tokenCount > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(55px, 1fr))',
            gap: '3px',
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            alignContent: 'center',
            userSelect: 'none',
            padding: '8px',
            position: 'relative',
            zIndex: 1,
            boxSizing: 'border-box',
            // Responsive adjustments
            '@media (max-width: 500px)': {
              gridTemplateColumns: 'repeat(auto-fit, minmax(45px, 1fr))',
              gap: '2px',
              padding: '6px',
            },
            '@media (max-width: 350px)': {
              gridTemplateColumns: 'repeat(auto-fit, minmax(40px, 1fr))',
              gap: '2px',
              padding: '4px',
            },
          }}
        >
          {tokenPattern.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                minWidth: '0',
                height: '12px',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '7px',
                fontWeight: 600,
                fontFamily: 'monospace',
                color: (theme) =>
                  item.isActive
                    ? theme.palette.mode === 'dark'
                      ? '#b278c8'
                      : '#944ba8'
                    : theme.palette.mode === 'dark'
                      ? 'rgba(180, 180, 180, 0.2)'
                      : 'rgba(102, 102, 102, 0.2)',
                animation: `${binaryPulse} 1.5s ease-in-out ${item.delay}s infinite`,
                textAlign: 'center',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                boxSizing: 'border-box',
                letterSpacing: '0.5px',
                // Responsive font size
                '@media (max-width: 500px)': {
                  fontSize: '8px',
                },
                '@media (max-width: 350px)': {
                  fontSize: '7px',
                },
              }}
            >
              {item.token}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AILoadingAnimation;
